import { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, LineChart, Line, Area, AreaChart } from 'recharts';
import NewsCard from '@/components/NewsCard';
import SEO from '@/components/SEO';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { 
  RefreshCw, AlertTriangle, TrendingUp, FileText, Users, MapPin, 
  Vote, Calendar, Download, Smartphone, ChevronRight, Flame, Clock,
  Building2, Zap, ArrowRight, Eye, Bot, Search, Bell, Shield,
  BarChart3, Target, Play, ChevronLeft, History, Sparkles, Globe,
  Trophy, PoundSterling
} from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Party colors - only for content tags, keeping UI neutral
const PARTY_CONFIG = {
  labour: { color: '#DC2626', label: 'Labour', shortLabel: 'LAB' },
  conservative: { color: '#2563EB', label: 'Conservative', shortLabel: 'CON' },
  libdem: { color: '#F97316', label: 'Lib Dem', shortLabel: 'LD' },
  snp: { color: '#EAB308', label: 'SNP', shortLabel: 'SNP' },
  reform: { color: '#06B6D4', label: 'Reform', shortLabel: 'REF' },
  green: { color: '#22C55E', label: 'Green', shortLabel: 'GRN' },
  plaid: { color: '#059669', label: 'Plaid', shortLabel: 'PC' },
  dup: { color: '#991B1B', label: 'DUP', shortLabel: 'DUP' },
  sinnfein: { color: '#15803D', label: 'Sinn Féin', shortLabel: 'SF' }
};

// Animated Counter Component
const AnimatedCounter = ({ value, duration = 2000, suffix = '' }) => {
  const [count, setCount] = useState(0);
  const countRef = useRef(null);

  useEffect(() => {
    const end = parseInt(value) || 0;
    if (end === 0) return;
    const stepTime = Math.max(duration / end, 10);
    let current = 0;
    
    const timer = setInterval(() => {
      current += Math.ceil(end / (duration / stepTime));
      if (current >= end) {
        setCount(end);
        clearInterval(timer);
      } else {
        setCount(current);
      }
    }, stepTime);

    return () => clearInterval(timer);
  }, [value, duration]);

  return <span ref={countRef}>{count.toLocaleString()}{suffix}</span>;
};

// Live Countdown Component with seconds - Clickable
const LiveCountdown = ({ targetDate, label, gradient, to }) => {
  const [timeLeft, setTimeLeft] = useState({ days: 0, hours: 0, minutes: 0, seconds: 0 });

  useEffect(() => {
    const calculateTimeLeft = () => {
      const difference = new Date(targetDate) - new Date();
      if (difference > 0) {
        setTimeLeft({
          days: Math.floor(difference / (1000 * 60 * 60 * 24)),
          hours: Math.floor((difference / (1000 * 60 * 60)) % 24),
          minutes: Math.floor((difference / 1000 / 60) % 60),
          seconds: Math.floor((difference / 1000) % 60)
        });
      }
    };

    calculateTimeLeft();
    const timer = setInterval(calculateTimeLeft, 1000);
    return () => clearInterval(timer);
  }, [targetDate]);

  const content = (
    <div className={`bg-gradient-to-br ${gradient} rounded-2xl p-4 text-white shadow-lg ${to ? 'hover:shadow-2xl hover:scale-[1.02] transition-all cursor-pointer' : ''}`}>
      <p className="text-xs font-bold uppercase tracking-wider opacity-80 mb-2">{label}</p>
      <div className="grid grid-cols-4 gap-2 text-center">
        <div className="bg-white/20 backdrop-blur rounded-lg p-2">
          <p className="text-2xl font-black">{timeLeft.days}</p>
          <p className="text-[10px] uppercase tracking-wider opacity-80">Days</p>
        </div>
        <div className="bg-white/20 backdrop-blur rounded-lg p-2">
          <p className="text-2xl font-black">{timeLeft.hours}</p>
          <p className="text-[10px] uppercase tracking-wider opacity-80">Hours</p>
        </div>
        <div className="bg-white/20 backdrop-blur rounded-lg p-2">
          <p className="text-2xl font-black">{timeLeft.minutes}</p>
          <p className="text-[10px] uppercase tracking-wider opacity-80">Mins</p>
        </div>
        <div className="bg-white/20 backdrop-blur rounded-lg p-2">
          <p className="text-2xl font-black animate-pulse">{timeLeft.seconds}</p>
          <p className="text-[10px] uppercase tracking-wider opacity-80">Secs</p>
        </div>
      </div>
      {to && (
        <div className="mt-3 text-xs font-medium opacity-70 flex items-center justify-center gap-1">
          View Predictions <ArrowRight className="h-3 w-3" />
        </div>
      )}
    </div>
  );

  if (to) {
    return <Link to={to}>{content}</Link>;
  }
  return content;
};

// Breaking News Ticker - Seamless Loop
const NewsTicker = ({ articles }) => {
  if (!articles?.length) return null;
  
  // Double the articles for seamless looping
  const tickerItems = [...articles.slice(0, 5), ...articles.slice(0, 5)];
  
  return (
    <div className="bg-gradient-to-r from-red-600 to-red-700 text-white py-2 overflow-hidden">
      <div className="flex items-center gap-4">
        <span className="flex-shrink-0 bg-white text-red-600 text-xs font-bold px-3 py-1 rounded-full tracking-wider uppercase animate-pulse ml-4">
          Breaking
        </span>
        <div className="overflow-hidden flex-1 relative">
          <div className="ticker-track flex gap-16">
            {tickerItems.map((article, i) => (
              <Link 
                key={`${article.id}-${i}`}
                to={`/article/${article.id}`}
                className="inline-flex items-center gap-2 hover:text-red-200 transition-colors whitespace-nowrap flex-shrink-0"
              >
                <span className="text-red-300">•</span>
                <span className="text-sm font-medium">{article.title}</span>
              </Link>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

// Feature Card Component - Colorful
const FeatureCard = ({ icon: Icon, title, description, to, gradient, delay = 0 }) => (
  <Link 
    to={to}
    className={`group relative bg-gradient-to-br ${gradient} rounded-2xl p-6 text-white shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:scale-[1.02]`}
    style={{ animationDelay: `${delay}ms` }}
    data-testid={`feature-${title.toLowerCase().replace(/\s+/g, '-')}`}
  >
    <div className="absolute top-0 right-0 w-24 h-24 bg-white/10 rounded-full -mr-12 -mt-12" />
    <div className="relative">
      <Icon className="h-8 w-8 mb-4 opacity-90" />
      <h3 className="font-bold text-xl mb-2">{title}</h3>
      <p className="text-sm text-white/80 leading-relaxed">{description}</p>
      <div className="mt-4 flex items-center gap-2 text-sm font-medium opacity-80 group-hover:opacity-100 group-hover:gap-3 transition-all">
        Learn More <ArrowRight className="h-4 w-4" />
      </div>
    </div>
  </Link>
);

// Data Widget Component - Colorful & Clickable
const DataWidget = ({ label, value, subtext, icon: Icon, gradient, to, onClick }) => {
  const handleClick = () => {
    if (onClick) {
      onClick();
    } else if (to === '#articles') {
      document.getElementById('articles')?.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const content = (
    <div 
      className={`bg-gradient-to-br ${gradient} rounded-2xl p-5 text-white shadow-lg ${(to || onClick) ? 'hover:shadow-2xl hover:scale-[1.02] transition-all cursor-pointer' : ''}`}
      onClick={to === '#articles' ? handleClick : undefined}
    >
      <div className="flex items-center justify-between mb-2">
        <span className="text-xs font-bold uppercase tracking-wider opacity-80">{label}</span>
        {Icon && <Icon className="h-5 w-5 opacity-70" />}
      </div>
      <div className="text-4xl font-black">
        <AnimatedCounter value={value} />
      </div>
      {subtext && <div className="text-sm opacity-70 mt-1">{subtext}</div>}
      {to && (
        <div className="mt-2 text-xs font-medium opacity-70 flex items-center gap-1">
          View Details <ArrowRight className="h-3 w-3" />
        </div>
      )}
    </div>
  );
  
  if (to && to !== '#articles') {
    return <Link to={to}>{content}</Link>;
  }
  return content;
};

// Trending Politician Row
const TrendingRow = ({ politician, rank }) => {
  const partyConfig = PARTY_CONFIG[politician.party?.toLowerCase()] || { color: '#6B7280', label: politician.party };
  const slug = politician.name?.toLowerCase().replace(/ /g, '-').replace(/'/g, '') || '';
  
  return (
    <Link
      to={`/politician/${slug}`}
      className="flex items-center gap-4 py-3 border-b border-slate-100 last:border-0 hover:bg-slate-50 -mx-4 px-4 transition-colors group"
    >
      <span className="font-mono text-sm text-slate-400 w-6">{String(rank).padStart(2, '0')}</span>
      <div 
        className="w-2 h-8"
        style={{ backgroundColor: partyConfig.color }}
      />
      <div className="flex-1 min-w-0">
        <div className="font-medium truncate group-hover:underline">{politician.name}</div>
        <div className="text-xs text-slate-500">{politician.politician_type || 'MP'}</div>
      </div>
      <div className="text-right">
        <div className="font-mono text-sm">{politician.mentions}</div>
        <div className="text-xs text-slate-400">mentions</div>
      </div>
      <ChevronRight className="h-4 w-4 text-slate-300 group-hover:text-black transition-colors" />
    </Link>
  );
};

export default function HomePage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [articles, setArticles] = useState([]);
  const [stats, setStats] = useState(null);
  const [electionStats, setElectionStats] = useState(null);
  const [regions, setRegions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedParty, setSelectedParty] = useState(searchParams.get('party') || '');
  const [selectedRegion, setSelectedRegion] = useState('');
  const [scraping, setScraping] = useState(false);
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [isAppInstalled, setIsAppInstalled] = useState(false);
  const [trendingPoliticians, setTrendingPoliticians] = useState([]);
  const [historicalData, setHistoricalData] = useState(null);
  const [topExpensesMPs, setTopExpensesMPs] = useState([]);

  useEffect(() => {
    fetchRegions();
    fetchStats();
    fetchElectionStats();
    fetchTrendingPoliticians();
    fetchHistoricalSummary();
    fetchTopExpensesMPs();
    
    if (window.matchMedia('(display-mode: standalone)').matches) {
      setIsAppInstalled(true);
    }
    
    const handleBeforeInstallPrompt = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
    };
    
    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    return () => window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
  }, []);

  const fetchTrendingPoliticians = async () => {
    try {
      const response = await axios.get(`${API}/politicians/trending`);
      setTrendingPoliticians(response.data.trending || []);
    } catch (error) {
      console.error('Failed to fetch trending politicians:', error);
    }
  };

  const fetchHistoricalSummary = async () => {
    try {
      const response = await axios.get(`${API}/election-results/historical`);
      if (response.data?.elections) {
        setHistoricalData(response.data);
      }
    } catch (error) {
      console.error('Failed to fetch historical data:', error);
    }
  };

  const fetchTopExpensesMPs = async () => {
    try {
      const response = await axios.get(`${API}/mp-expenses/leaderboard`, {
        params: { limit: 5, sort_by: 'total', order: 'desc', current_only: true }
      });
      setTopExpensesMPs(response.data.leaderboard || []);
    } catch (error) {
      console.error('Failed to fetch MP expenses:', error);
    }
  };

  const handleInstallClick = async () => {
    if (deferredPrompt) {
      deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      if (outcome === 'accepted') {
        setIsAppInstalled(true);
        toast.success('App installed successfully!');
      }
      setDeferredPrompt(null);
    } else {
      toast.info('To install: tap the share button and select "Add to Home Screen"');
    }
  };

  useEffect(() => {
    fetchArticles();
  }, [selectedParty, selectedRegion]);

  const fetchRegions = async () => {
    try {
      const response = await axios.get(`${API}/regions`);
      setRegions(response.data.regions);
    } catch (error) {
      console.error('Failed to fetch regions:', error);
    }
  };

  const fetchArticles = async () => {
    setLoading(true);
    try {
      const params = {};
      if (selectedParty && selectedParty !== 'all') params.party = selectedParty;
      if (selectedRegion && selectedRegion !== 'all') params.region = selectedRegion;
      
      const response = await axios.get(`${API}/articles`, { params });
      setArticles(response.data);
    } catch (error) {
      console.error('Failed to fetch articles:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API}/stats`);
      setStats(response.data);
    } catch (error) {
      console.error('Failed to fetch stats:', error);
    }
  };

  const fetchElectionStats = async () => {
    try {
      const response = await axios.get(`${API}/elections/stats`);
      setElectionStats(response.data);
    } catch (error) {
      console.error('Failed to fetch election stats:', error);
    }
  };

  const handleScrape = async () => {
    setScraping(true);
    try {
      await axios.post(`${API}/scrape`);
      toast.success('News scan initiated. New articles appearing shortly.');
      setTimeout(() => {
        fetchArticles();
        fetchStats();
      }, 30000);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to trigger scrape');
    } finally {
      setScraping(false);
    }
  };

  const clearFilters = () => {
    setSelectedParty('');
    setSelectedRegion('');
  };

  // Prepare chart data for historical preview
  const historicalChartData = historicalData?.years?.slice(-8).map(year => ({
    year,
    con: historicalData.elections[year]?.conservative || 0,
    lab: historicalData.elections[year]?.labour || 0,
  })) || [];

  return (
    <div className="min-h-screen bg-white" data-testid="home-page">
      <SEO 
        title={null}
        description="Track UK political scandals and hold MPs accountable. Independent news aggregation covering Westminster and local councils. 649 MPs, 19,000+ councillors monitored 24/7."
        keywords="UK political news, MP scandals, councillor corruption, Westminster news, political accountability UK, track politicians"
      />
      
      {/* Breaking News Ticker */}
      {articles.length > 0 && <NewsTicker articles={articles} />}

      {/* Hero Section - Centered & Colorful */}
      <section className="relative bg-gradient-to-br from-slate-900 via-blue-900 to-purple-900 overflow-hidden" data-testid="hero-section">
        {/* Animated Background */}
        <div className="absolute inset-0 opacity-20">
          <div className="absolute inset-0" style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.4'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
          }} />
        </div>
        
        {/* Gradient Orbs */}
        <div className="absolute top-0 left-1/4 w-96 h-96 bg-blue-500/30 rounded-full blur-3xl" />
        <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-purple-500/30 rounded-full blur-3xl" />
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-24">
          {/* Centered Hero Content */}
          <div className="text-center max-w-4xl mx-auto mb-12">
            <div className="inline-flex items-center gap-3 px-5 py-2 bg-white/10 backdrop-blur rounded-full mb-6">
              <Zap className="h-4 w-4 text-yellow-400" />
              <span className="text-sm font-medium text-white">Political Accountability Platform</span>
              <span className="text-xs text-white/60">Est. 2025</span>
            </div>
            
            <h1 className="font-serif text-5xl md:text-7xl lg:text-8xl font-light tracking-tight leading-[0.9] text-white mb-6">
              Holding{' '}
              <span className="bg-gradient-to-r from-yellow-400 via-orange-400 to-red-400 bg-clip-text text-transparent font-normal italic">
                Power
              </span>
              <br />Accountable
            </h1>
            
            <p className="text-xl text-slate-300 leading-relaxed max-w-2xl mx-auto mb-8">
              Real-time monitoring of UK political misconduct. We aggregate, verify, 
              and expose what those in power would rather keep hidden.
            </p>
            
            <div className="flex flex-wrap gap-4 justify-center">
              <Link to="/submit-tip">
                <Button 
                  className="bg-gradient-to-r from-red-500 to-red-600 hover:from-red-600 hover:to-red-700 text-white font-bold px-8 py-4 rounded-xl shadow-lg hover:shadow-xl transition-all text-base"
                  data-testid="hero-submit-tip"
                >
                  <Shield className="h-5 w-5 mr-2" />
                  Submit a Tip
                </Button>
              </Link>
              <Button 
                onClick={handleScrape} 
                disabled={scraping}
                variant="outline"
                className="bg-white/10 backdrop-blur border-white/30 text-white hover:bg-white/20 font-bold px-8 py-4 rounded-xl text-base"
                data-testid="refresh-news"
              >
                <RefreshCw className={`h-5 w-5 mr-2 ${scraping ? 'animate-spin' : ''}`} />
                {scraping ? 'Scanning...' : 'Scan News'}
              </Button>
            </div>
            
            {!isAppInstalled && (
              <button
                onClick={handleInstallClick}
                className="mt-6 inline-flex items-center gap-2 px-5 py-3 bg-gradient-to-r from-blue-500 to-purple-500 text-white font-bold rounded-full hover:from-blue-600 hover:to-purple-600 transition-all shadow-lg"
                data-testid="install-app-button"
              >
                <Smartphone className="h-5 w-5" />
                Install App
                <Download className="h-5 w-5" />
              </button>
            )}
          </div>
          
          {/* Live Data Grid - Centered */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 max-w-4xl mx-auto">
            <DataWidget 
              label="MPs Tracked" 
              value={electionStats?.total_mps || 649} 
              subtext="Westminster" 
              icon={Users}
              gradient="from-blue-600 to-blue-500"
              to="/politicians"
            />
            <DataWidget 
              label="Councils" 
              value={electionStats?.total_councils || 382} 
              subtext="Local Authorities" 
              icon={Building2}
              gradient="from-emerald-600 to-emerald-500"
              to="/council-odds"
            />
            <DataWidget 
              label="Councillors" 
              value={electionStats?.total_councillors || 19107} 
              subtext="Across UK" 
              icon={Users}
              gradient="from-purple-600 to-purple-500"
              to="/politicians"
            />
            <DataWidget 
              label="Articles" 
              value={stats?.total_articles || 0} 
              subtext="Aggregated" 
              icon={FileText}
              gradient="from-slate-700 to-slate-600"
              to="#articles"
            />
          </div>
          
          {/* Election Countdowns - Centered */}
          {electionStats && (
            <div className="grid md:grid-cols-2 gap-4 max-w-2xl mx-auto mt-6">
              <LiveCountdown 
                targetDate={electionStats?.election_countdowns?.local_elections?.date || '2026-05-07'}
                label="Next Local Elections"
                gradient="from-cyan-600 via-cyan-500 to-blue-500"
                to="/council-odds"
              />
              <LiveCountdown 
                targetDate={electionStats?.election_countdowns?.general_election?.date || '2029-05-03'}
                label="General Election"
                gradient="from-pink-600 via-pink-500 to-purple-500"
                to="/odds"
              />
            </div>
          )}
        </div>
      </section>

      {/* Tools & Data Section - Centered & Colorful */}
      <section className="py-16 bg-gradient-to-b from-slate-50 to-white" data-testid="tools-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="font-serif text-4xl md:text-5xl font-normal tracking-tight mb-4">Tools & Data</h2>
            <p className="text-xl text-slate-500 max-w-2xl mx-auto">Everything you need to investigate UK politics</p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 stagger-children">
            <FeatureCard 
              icon={Target}
              title="MP Seat Odds"
              description="Predicted winners for all 650 UK constituencies. Interactive polling sliders."
              to="/odds"
              gradient="from-blue-600 to-indigo-600"
              delay={0}
            />
            <FeatureCard 
              icon={Building2}
              title="Council Odds"
              description="Seat projections for 382 local councils. See which parties could gain control."
              to="/council-odds"
              gradient="from-emerald-600 to-teal-600"
              delay={100}
            />
            <FeatureCard 
              icon={History}
              title="Election History"
              description="70 years of UK elections visualized. From 1955 to 2024 + future projections."
              to="/election-history"
              gradient="from-amber-500 to-orange-500"
              delay={200}
            />
            <FeatureCard 
              icon={Bot}
              title="AI Research"
              description="Ask questions about any UK politician. AI searches records to find answers."
              to="/ai-research"
              gradient="from-purple-600 to-pink-600"
              delay={300}
            />
            <FeatureCard 
              icon={Users}
              title="Politician Database"
              description="Search and compare MPs and councillors. Voting records, contact info."
              to="/politicians"
              gradient="from-cyan-600 to-blue-600"
              delay={400}
            />
            <FeatureCard 
              icon={Globe}
              title="Interactive Map"
              description="Geographic view of UK political landscape. Search by postcode."
              to="/map"
              gradient="from-rose-600 to-red-600"
              delay={500}
            />
          </div>
        </div>
      </section>

      {/* MP Expenses Leaderboard Section */}
      {topExpensesMPs.length > 0 && (
        <section className="py-10 sm:py-16 bg-gradient-to-br from-emerald-900 via-teal-900 to-cyan-900" data-testid="mp-expenses-section">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-6 sm:mb-10">
              <div className="inline-flex items-center gap-2 px-3 sm:px-4 py-1.5 sm:py-2 bg-white/10 backdrop-blur rounded-full mb-3 sm:mb-4">
                <Trophy className="h-3 w-3 sm:h-4 sm:w-4 text-amber-400" />
                <span className="text-xs sm:text-sm font-medium text-white">2024-25 Financial Year</span>
              </div>
              <h2 className="font-serif text-2xl sm:text-4xl md:text-5xl font-light text-white mb-2 sm:mb-4">MP Expenses</h2>
              <p className="text-sm sm:text-xl text-slate-300 max-w-2xl mx-auto">
                See which MPs claim the most in taxpayer-funded expenses
              </p>
            </div>
            
            {/* Top 5 Leaderboard Cards */}
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-2 sm:gap-3 max-w-4xl mx-auto mb-6 sm:mb-8">
              {topExpensesMPs.map((mp, index) => {
                const slug = mp.name?.toLowerCase().replace(/ /g, '-').replace(/'/g, '') || '';
                const formatCurrency = (amount) => new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP', minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(amount);
                return (
                  <Link
                    key={mp.name}
                    to={`/politician/${slug}`}
                    className={`block cursor-pointer rounded-xl p-2.5 sm:p-4 transition-all hover:scale-105 ${
                      index === 0 ? 'bg-gradient-to-br from-amber-400 to-yellow-500 text-slate-900' :
                      index === 1 ? 'bg-gradient-to-br from-slate-300 to-slate-400 text-slate-900' :
                      index === 2 ? 'bg-gradient-to-br from-amber-600 to-amber-700 text-white' :
                      'bg-white/10 text-white hover:bg-white/20'
                    }`}
                    data-testid={`home-expense-card-${index + 1}`}
                  >
                    <div className={`text-xl sm:text-2xl md:text-3xl font-black mb-0.5 sm:mb-1 ${index < 3 ? '' : 'text-emerald-400'}`}>
                      #{index + 1}
                    </div>
                    <div className="font-bold text-[11px] sm:text-xs md:text-sm truncate">{mp.name}</div>
                    <div className={`text-[10px] sm:text-xs truncate mb-1 sm:mb-2 ${index < 3 ? 'opacity-70' : 'text-slate-400'}`}>
                      {mp.constituency || 'N/A'}
                    </div>
                    <div className="font-black text-xs sm:text-sm md:text-lg">{formatCurrency(mp.total_spend)}</div>
                  </Link>
                );
              })}
            </div>
            
            {/* CTA Button */}
            <div className="text-center">
              <Link to="/mp-expenses">
                <Button className="bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white font-bold px-4 sm:px-8 py-2.5 sm:py-4 rounded-xl shadow-lg text-sm sm:text-base">
                  <PoundSterling className="h-4 w-4 sm:h-5 sm:w-5 mr-1.5 sm:mr-2" />
                  View Full Leaderboard
                </Button>
              </Link>
            </div>
          </div>
        </section>
      )}

      {/* Historical Preview + Projections - Centered */}
      {historicalChartData.length > 0 && (
        <section className="py-16 bg-gradient-to-br from-slate-900 via-slate-800 to-indigo-900 text-white" data-testid="historical-section">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-10">
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/10 backdrop-blur rounded-full mb-4">
                <History className="h-4 w-4 text-amber-400" />
                <span className="text-sm font-medium">Electoral Archive</span>
              </div>
              <h2 className="font-serif text-4xl md:text-5xl font-light mb-4">70 Years of Elections</h2>
              <p className="text-xl text-slate-300 max-w-2xl mx-auto">
                From Thatcher to Blair to Brexit — see how Britain voted
              </p>
            </div>
            
            {/* Chart - Centered */}
            <div className="max-w-4xl mx-auto mb-10">
              <div className="h-72 bg-white/5 backdrop-blur rounded-2xl p-6 border border-white/10">
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={historicalChartData}>
                    <XAxis 
                      dataKey="year" 
                      tick={{ fontSize: 11, fill: '#94a3b8' }}
                      tickLine={false}
                      axisLine={{ stroke: '#334155' }}
                    />
                    <YAxis 
                      tick={{ fontSize: 11, fill: '#94a3b8' }}
                      tickLine={false}
                      axisLine={false}
                      width={40}
                    />
                    <Tooltip 
                      contentStyle={{ 
                        background: '#1e293b',
                        border: '1px solid #334155', 
                        borderRadius: 8,
                        fontSize: 12,
                        color: '#fff'
                      }}
                    />
                    <Area 
                      type="monotone" 
                      dataKey="con" 
                      stackId="1"
                      stroke="#2563EB" 
                      fill="#2563EB"
                      fillOpacity={0.8}
                      name="Conservative"
                    />
                    <Area 
                      type="monotone" 
                      dataKey="lab" 
                      stackId="1"
                      stroke="#DC2626" 
                      fill="#DC2626"
                      fillOpacity={0.8}
                      name="Labour"
                    />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            </div>
            
            {/* CTAs - Centered */}
            <div className="flex flex-wrap gap-4 justify-center">
              <Link to="/election-history">
                <Button className="bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white font-bold px-8 py-4 rounded-xl shadow-lg">
                  <History className="h-5 w-5 mr-2" />
                  Explore Full Timeline
                </Button>
              </Link>
              <Link to="/odds">
                <Button variant="outline" className="bg-white/10 border-white/30 text-white hover:bg-white/20 font-bold px-8 py-4 rounded-xl">
                  <Target className="h-5 w-5 mr-2" />
                  View 2029 Predictions
                </Button>
              </Link>
            </div>
          </div>
        </section>
      )}

      {/* Councillor Watch Section - Centered & Colorful */}
      <section className="py-16 bg-gradient-to-b from-white to-slate-50" data-testid="councillor-watch-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-emerald-100 rounded-full mb-4">
              <Eye className="h-4 w-4 text-emerald-600" />
              <span className="text-sm font-bold text-emerald-700">Councillor Watch</span>
            </div>
            <h2 className="font-serif text-4xl md:text-5xl font-normal tracking-tight mb-4">Track Your Representatives</h2>
            <p className="text-xl text-slate-500 max-w-2xl mx-auto">
              Monitor any MP or councillor in the UK. Get alerts when they appear in the news.
            </p>
          </div>
          
          <div className="grid lg:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {/* Action Cards */}
            <div className="space-y-4">
              <Link to="/politicians" className="block">
                <div className="bg-gradient-to-br from-emerald-500 to-teal-600 rounded-2xl p-6 text-white shadow-lg hover:shadow-xl transition-shadow">
                  <Search className="h-8 w-8 mb-4" />
                  <h3 className="font-bold text-xl mb-2">Search Politicians</h3>
                  <p className="text-white/80">Find any MP or councillor by name, party, or constituency</p>
                </div>
              </Link>
              <Link to="/settings" className="block">
                <div className="bg-gradient-to-br from-amber-500 to-orange-500 rounded-2xl p-6 text-white shadow-lg hover:shadow-xl transition-shadow">
                  <Bell className="h-8 w-8 mb-4" />
                  <h3 className="font-bold text-xl mb-2">News Alerts</h3>
                  <p className="text-white/80">Get SMS & email notifications when they're in the news</p>
                </div>
              </Link>
              <Link to="/ai-research" className="block">
                <div className="bg-gradient-to-br from-purple-500 to-pink-500 rounded-2xl p-6 text-white shadow-lg hover:shadow-xl transition-shadow">
                  <Bot className="h-8 w-8 mb-4" />
                  <h3 className="font-bold text-xl mb-2">AI Research</h3>
                  <p className="text-white/80">Ask questions about anyone - AI finds the answers</p>
                </div>
              </Link>
            </div>
            
            {/* Trending List */}
            <div className="lg:col-span-2 bg-white rounded-2xl shadow-lg border border-slate-200 p-6">
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-2">
                  <Flame className="h-6 w-6 text-orange-500" />
                  <h3 className="font-bold text-xl">Trending in the News</h3>
                </div>
                <Link to="/politicians" className="text-sm text-emerald-600 hover:text-emerald-700 font-medium flex items-center gap-1">
                  View All <ChevronRight className="h-4 w-4" />
                </Link>
              </div>
              
              {trendingPoliticians.length > 0 ? (
                <div className="divide-y divide-slate-100">
                  {trendingPoliticians.slice(0, 6).map((politician, idx) => (
                    <TrendingRow key={idx} politician={politician} rank={idx + 1} />
                  ))}
                </div>
              ) : (
                <div className="text-center py-16 text-slate-400">
                  <Users className="h-16 w-16 mx-auto mb-4 opacity-50" />
                  <p className="text-lg">No trending politicians yet</p>
                  <p className="text-sm">Check back after news scanning</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Party Article Stats - Centered */}
      {stats?.by_party && (
        <section className="py-12 bg-white" data-testid="party-stats-section">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-8">
              <h2 className="font-serif text-3xl mb-2">Articles by Party</h2>
              <p className="text-slate-500">Click any party to filter articles</p>
            </div>
            
            <div className="flex flex-wrap gap-3 justify-center">
              {Object.entries(stats.by_party || {})
                .filter(([_, count]) => count > 0)
                .sort((a, b) => b[1] - a[1])
                .map(([party, count]) => {
                  const config = PARTY_CONFIG[party] || { color: '#6B7280', label: party };
                  const isSelected = selectedParty === party;
                  return (
                    <button
                      key={party}
                      onClick={() => setSelectedParty(isSelected ? '' : party)}
                      className={`group flex items-center gap-2 px-5 py-3 rounded-full transition-all shadow-sm hover:shadow-md ${
                        isSelected 
                          ? 'text-white shadow-lg scale-105' 
                          : 'bg-white border-2 hover:scale-105'
                      }`}
                      style={{ 
                        backgroundColor: isSelected ? config.color : undefined,
                        borderColor: isSelected ? config.color : config.color + '40'
                      }}
                    >
                      <span 
                        className="w-3 h-3 rounded-full"
                        style={{ backgroundColor: isSelected ? 'white' : config.color }}
                      />
                      <span className="font-medium" style={{ color: isSelected ? 'white' : config.color }}>
                        {config.label}
                      </span>
                      <span className={`font-mono text-sm ${isSelected ? 'text-white/80' : 'text-slate-400'}`}>
                        {count}
                      </span>
                    </button>
                  );
                })}
            </div>
            
            {selectedParty && (
              <div className="mt-6 text-center">
                <button 
                  onClick={clearFilters}
                  className="text-sm text-slate-500 hover:text-slate-700 font-medium"
                >
                  Clear filter ×
                </button>
              </div>
            )}
          </div>
        </section>
      )}

      {/* Region Filter - Centered */}
      <section className="sticky top-14 z-40 bg-white/95 backdrop-blur border-b border-slate-200 py-4" id="articles">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <h2 className="font-serif text-xl text-center">
              {selectedParty 
                ? `${PARTY_CONFIG[selectedParty]?.label || selectedParty} Reports` 
                : 'Latest Reports'}
            </h2>
            
            <Select value={selectedRegion} onValueChange={setSelectedRegion}>
              <SelectTrigger className="w-56 h-10 rounded-xl border-slate-300" data-testid="region-filter">
                <MapPin className="h-4 w-4 mr-2 text-slate-400" />
                <SelectValue placeholder="All Regions" />
              </SelectTrigger>
              <SelectContent className="max-h-64 rounded-xl">
                <SelectItem value="all">All Regions</SelectItem>
                {regions.map(region => (
                  <SelectItem key={region} value={region}>{region}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
      </section>

      {/* Articles Grid - Centered */}
      <section className="py-12 bg-slate-50" data-testid="articles-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {loading ? (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[1, 2, 3, 4, 5, 6].map(i => (
                <div key={i} className="bg-white rounded-2xl p-6 shadow-sm animate-pulse">
                  <div className="h-4 bg-slate-200 rounded w-20 mb-4"></div>
                  <div className="h-6 bg-slate-200 rounded w-full mb-2"></div>
                  <div className="h-6 bg-slate-200 rounded w-3/4 mb-4"></div>
                  <div className="h-20 bg-slate-200 rounded"></div>
                </div>
              ))}
            </div>
          ) : articles.length === 0 ? (
            <div className="py-24 text-center bg-white rounded-2xl shadow-sm max-w-2xl mx-auto">
              <FileText className="h-16 w-16 mx-auto text-slate-300 mb-4" />
              <h3 className="text-xl font-medium mb-2">No articles found</h3>
              <p className="text-slate-500 mb-6">
                {selectedParty || selectedRegion 
                  ? 'Try adjusting your filters'
                  : 'Click "Scan News" to aggregate new articles'}
              </p>
              <div className="flex justify-center gap-4">
                {(selectedParty || selectedRegion) && (
                  <Button 
                    variant="outline" 
                    onClick={clearFilters} 
                    className="rounded-xl"
                  >
                    Clear Filters
                  </Button>
                )}
                <Button 
                  onClick={handleScrape} 
                  disabled={scraping} 
                  className="bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white rounded-xl"
                >
                  <RefreshCw className={`h-4 w-4 mr-2 ${scraping ? 'animate-spin' : ''}`} />
                  Scan for News
                </Button>
              </div>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {articles.map((article, index) => (
                <NewsCard 
                  key={article.id} 
                  article={article} 
                  featured={index === 0 && !selectedParty}
                />
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Whistleblower CTA - Centered & Colorful */}
      <section className="bg-gradient-to-br from-slate-900 via-emerald-900 to-slate-900 text-white py-20" data-testid="whistleblower-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-emerald-500/20 backdrop-blur rounded-full mb-6">
              <Shield className="h-4 w-4 text-emerald-400" />
              <span className="text-sm font-bold text-emerald-300">Secure & Anonymous</span>
            </div>
            
            <h2 className="font-serif text-5xl md:text-6xl font-light mb-6">
              Know Something?
            </h2>
            <p className="text-xl text-slate-300 max-w-2xl mx-auto mb-8">
              Our whistleblower platform provides anonymous, encrypted submission of tips and documents. 
              Your identity is protected by design.
            </p>
            
            <Link to="/submit-tip">
              <Button className="bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-600 hover:to-teal-600 text-white font-bold px-10 py-5 rounded-xl shadow-lg text-lg">
                <Shield className="h-6 w-6 mr-3" />
                Submit Securely
              </Button>
            </Link>
            
            {/* Terminal Preview - Centered */}
            <div className="mt-12 bg-black/50 backdrop-blur border border-slate-700 rounded-2xl p-6 font-mono text-sm max-w-xl mx-auto text-left">
              <div className="flex items-center gap-2 mb-4">
                <div className="w-3 h-3 rounded-full bg-red-500" />
                <div className="w-3 h-3 rounded-full bg-yellow-500" />
                <div className="w-3 h-3 rounded-full bg-green-500" />
              </div>
              <div className="text-emerald-400">
                <p className="mb-2">$ watchtower --submit-tip</p>
                <p className="text-slate-500 mb-2">&gt; Initializing secure connection...</p>
                <p className="text-slate-500 mb-2">&gt; Encrypting payload...</p>
                <p className="text-emerald-400">&gt; Connection established. You are anonymous.</p>
                <p className="text-emerald-300 mt-4 animate-pulse">_</p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
